import { useEffect, useRef } from "react";

const POLL_INTERVAL_MS = 60_000;

/**
 * Núcleo reusable: hace polling de una función que resuelve un string de
 * versión, guarda el primer valor visto como baseline, y fuerza un refresh
 * (con cache-busting) en cuanto detecta un valor distinto. Usado tanto para
 * detectar deploys del backend (useBackendVersionWatcher) como del propio
 * POS (usePosVersionWatcher) — misma estrategia, distinta fuente.
 */
export const useVersionWatcher = (getVersion: () => Promise<string | null | undefined>, label: string) => {
  const knownVersion = useRef<string | null>(null);

  useEffect(() => {
    const checkVersion = () => {
      getVersion()
        .then((version) => {
          if (!version) return;

          if (knownVersion.current === null) {
            knownVersion.current = version;
            return;
          }

          if (version !== knownVersion.current) {
            console.log(`[POS] Nueva versión de ${label} detectada (${knownVersion.current} -> ${version}), refrescando...`);
            // location.reload() puede servir index.html desde cache del navegador
            // si el hosting no manda headers de revalidación. Se navega con un
            // query param único para forzar que el navegador lo trate como un
            // recurso nuevo y sí traiga el index.html (y por lo tanto los
            // bundles .js/.css con hash nuevo) desde el servidor.
            const url = new URL(window.location.href);
            url.searchParams.set("_v", Date.now().toString());
            window.location.href = url.toString();
          }
        })
        .catch((err) => {
          console.error(`No se pudo consultar la versión de ${label}`, err);
        });
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [getVersion, label]);
};
